import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface SearchOption {
  label: string;
  value: string;
  [key: string]: unknown;
}

interface AsyncSearchInputProps {
  inputValue: string;
  onInputChange: (value: string) => void;
  onSelect: (option: SearchOption) => void;
  onClear: () => void;
  loadOptions: (inputValue: string) => Promise<SearchOption[]>;
  placeholder?: string;
  styles?: React.CSSProperties;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}

const AsyncSearchInput: React.FC<AsyncSearchInputProps> = ({
  inputValue,
  onInputChange,
  onSelect,
  onClear,
  loadOptions,
  placeholder = 'Search...',
  styles: _styles,
  onKeyDown,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [options, setOptions] = useState<SearchOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false); // Track if we've completed at least one search
  const [hoveredIndex, setHoveredIndex] = useState<number>(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const loadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const justSelectedRef = useRef<boolean>(false); // Track if we just selected an option to prevent re-fetching
  const lastSearchedTermRef = useRef<string>(''); // Track the last term we actually searched for

  // Update dropdown position when it opens or on scroll/resize
  const updateDropdownPosition = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 8, // 8px gap
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Update position when dropdown opens or window resizes/scrolls
  useEffect(() => {
    if (showDropdown) {
      updateDropdownPosition();

      const handleScroll = () => updateDropdownPosition();
      const handleResize = () => updateDropdownPosition();

      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [showDropdown]);

  // Load options when input changes
  useEffect(() => {
    // Clear any pending timeouts first
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
    }

    // If we just selected an option, skip the API call
    if (justSelectedRef.current) {
      justSelectedRef.current = false; // Reset the flag
      loadTimeoutRef.current = null; // Clear the ref when skipping
      return;
    }

    if (inputValue.length >= 2) {
      // Set state updates that will batch together
      setIsLoading(true);
      setHasSearched(false);
      setOptions([]);
      setShowDropdown(true);

      // Create timeout and store reference IMMEDIATELY (before any re-render)
      // This way, the render logic can check if a timeout is pending
      loadTimeoutRef.current = setTimeout(async () => {
        const searchTerm = inputValue; // Capture the current search term
        try {
          const results = await loadOptions(inputValue);
          // Update options and mark search as complete
          setOptions(results);
          setHasSearched(true);
          setIsLoading(false);
          lastSearchedTermRef.current = searchTerm; // Track what we just searched for
          loadTimeoutRef.current = null; // Clear ref after completion
        } catch (_error) {
          // On error, set empty options and mark search as complete
          setOptions([]);
          setHasSearched(true);
          setIsLoading(false);
          lastSearchedTermRef.current = searchTerm; // Track what we just searched for
          loadTimeoutRef.current = null; // Clear ref after error
        }
      }, 500);
    } else {
      // Input is too short, reset everything
      setOptions([]);
      setShowDropdown(false);
      setIsLoading(false);
      setHasSearched(false);
      lastSearchedTermRef.current = ''; // Reset last searched term
      loadTimeoutRef.current = null; // No pending timeout
    }

    return () => {
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
      }
    };
  }, [inputValue, loadOptions]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // User is manually typing, so reset the justSelected flag
    justSelectedRef.current = false;
    onInputChange(e.target.value);
    setHoveredIndex(-1);
  };

  const handleFocus = () => {
    setIsFocused(true);
    // Show dropdown if there are cached options, if we're loading, or if input is ready for search
    if (inputValue.length >= 2) {
      if (options.length > 0 || isLoading) {
        // Show dropdown if we have options or if we're currently loading
        setShowDropdown(true);
      }
    }
  };

  const handleOptionClick = (option: SearchOption) => {
    // Set flag to prevent API call when inputValue changes after selection
    justSelectedRef.current = true;

    // Clear any pending API calls
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }

    onSelect(option);
    setShowDropdown(false);
    setIsFocused(false);
    setOptions([]); // Clear options after selection
    setIsLoading(false); // Ensure loading state is cleared
    setHasSearched(false); // Reset search state after selection
    lastSearchedTermRef.current = ''; // Reset last searched term
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClear();
    setOptions([]);
    setShowDropdown(false);
    setHasSearched(false); // Reset search state when clearing
    lastSearchedTermRef.current = ''; // Reset last searched term
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (onKeyDown) {
      onKeyDown(e);
    }

    // Handle arrow navigation
    if (showDropdown && options.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHoveredIndex(prev => (prev < options.length - 1 ? prev + 1 : prev));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHoveredIndex(prev => (prev > 0 ? prev - 1 : -1));
      } else if (e.key === 'Enter' && hoveredIndex >= 0) {
        e.preventDefault();
        handleOptionClick(options[hoveredIndex]);
      } else if (e.key === 'Escape') {
        setShowDropdown(false);
      }
    }
  };

  return (
    <div className="async-search-input-wrapper" style={{ position: 'relative', width: '100%' }}>
      <input
        ref={inputRef}
        type="text"
        className="search-input"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        style={{
          width: '100%',
          height: '5rem',
          padding: '1.5rem 5rem 1.5rem 2rem',
          fontSize: '1.5rem',
          border: 'none',
          borderRadius: 'var(--border-radius-lg)',
          boxShadow: isFocused
            ? '0 20px 40px rgba(0,0,0,0.2), 0 0 0 4px rgba(59, 130, 246, 0.1)'
            : 'var(--shadow-xl)',
          outline: 'none',
          background: isFocused ? 'white' : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          transition: 'all 0.4s ease',
          transform: isFocused ? 'translateY(-3px)' : 'none',
          fontFamily: 'inherit',
          color: '#000000',
        }}
      />

      {/* Clear button */}
      {inputValue && (
        <button
          onClick={handleClear}
          style={{
            position: 'absolute',
            right: '5rem',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontSize: '1.5rem',
            color: '#9CA3AF',
            padding: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.color = '#6B7280';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.color = '#9CA3AF';
          }}
          aria-label="Clear search"
        >
          ×
        </button>
      )}

      {/* Dropdown - Rendered using Portal for proper z-index */}
      {showDropdown &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: 'absolute',
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
              backgroundColor: 'white',
              borderRadius: 'var(--border-radius)',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
              maxHeight: '400px',
              overflowY: 'auto',
              zIndex: 999999,
              padding: '0.5rem',
            }}
          >
            {isLoading || loadTimeoutRef.current ? (
              // Show "Searching..." while API is being called OR while timeout is pending
              // This prevents showing "No results" during React's state batching
              <div
                style={{
                  padding: '1rem',
                  textAlign: 'center',
                  color: '#6B7280',
                  fontSize: '1rem',
                }}
              >
                Searching...
              </div>
            ) : options.length > 0 ? (
              // Show options when results are available
              options.map((option, index) => (
                <div
                  key={`${option.value}-${index}`}
                  onClick={() => handleOptionClick(option)}
                  onMouseEnter={() => setHoveredIndex(index)}
                  style={{
                    padding: '1rem',
                    cursor: 'pointer',
                    borderRadius: '0.5rem',
                    marginBottom: '0.25rem',
                    backgroundColor:
                      hoveredIndex === index ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                    transition: 'all 0.2s ease',
                    fontSize: '1rem',
                    color: '#000000',
                  }}
                >
                  {option.label}
                </div>
              ))
            ) : hasSearched &&
              !isLoading &&
              !loadTimeoutRef.current &&
              inputValue.length >= 2 &&
              inputValue === lastSearchedTermRef.current ? (
              // Only show "No results found" when ALL FIVE conditions are met:
              // 1. Search has completed (hasSearched = true)
              // 2. NOT currently loading (isLoading = false)
              // 3. NO pending timeout (loadTimeoutRef.current = null)
              // 4. Input is valid length (>= 2 characters)
              // 5. Current input matches what we just searched (prevents stale state issues)
              <div
                style={{
                  padding: '1rem',
                  textAlign: 'center',
                  color: '#6B7280',
                  fontSize: '1rem',
                }}
              >
                No results found
              </div>
            ) : inputValue.length < 2 ? (
              // Show hint when input is too short
              <div
                style={{
                  padding: '1rem',
                  textAlign: 'center',
                  color: '#6B7280',
                  fontSize: '1rem',
                }}
              >
                Type at least 2 characters to search...
              </div>
            ) : (
              // Fallback to "Searching..." for any other state
              <div
                style={{
                  padding: '1rem',
                  textAlign: 'center',
                  color: '#6B7280',
                  fontSize: '1rem',
                }}
              >
                Searching...
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default AsyncSearchInput;
